import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getAllCabs, addCab, addCabSimple, updateCab, deleteCab } from '../../features/admin/adminSlice';

// API URL from centralized config
const { API_URL } = require('../../config/apiConfig');

// Custom CSS for toggle switch
const toggleStyles = `
  .toggle-checkbox {
    right: 0;
    z-index: 5;
    transition-property: transform;
  }
  input:checked + .toggle-label {
    background-color: #10B981;
  }
  input:checked + .toggle-label + .toggle-text:before {
    content: "Active";
  }
  input:not(:checked) + .toggle-label + .toggle-text:before {
    content: "Inactive";
  }
  input:checked + .toggle-label .toggle-checkbox {
    transform: translateX(100%);
    border-color: #ffffff;
  }
  .toggle-label {
    transition-property: background-color;
    transition-duration: 300ms;
  }
`;

const Cabs = () => {
  const dispatch = useDispatch();
  const { cabs, isLoading, isError, isSuccess, message } = useSelector((state) => state.admin);
  
  // Add the toggle styles to the document
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = toggleStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [currentCab, setCurrentCab] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [featureInput, setFeatureInput] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  // Fetch all cabs on component mount
  useEffect(() => {
    dispatch(getAllCabs());
  }, [dispatch]);
  
  // Show toast messages on success or error (with toastId to prevent duplicates)
  useEffect(() => {
    if (isError) {
      const msg = String(message || "");
      // Suppress unrelated wallet summary errors on this page to avoid duplicate toasts
      if (!/wallet summary/i.test(msg)) {
        toast.error(msg, { toastId: 'admin-error' });
      }
    }

    if (isSuccess && message) {
      const msg = String(message || "");
      // Avoid duplicate success toasts for cab operations (handled by the form flow)
      if (!/\bcab\b/i.test(msg) && !/cab created|cab updated|image uploaded/i.test(msg)) {
        toast.success(msg, { toastId: 'admin-success' });
      }
    }
  }, [isError, isSuccess, message]);
  
  // Form state for creating/editing cabs
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    acType: 'AC',
    seatingCapacity: 4,
    luggageCapacity: 2,
    baseKmPrice: 0,
    extraFarePerKm: 0,
    includedKm: 0,
    vehicleLocation: '',
    fuelCharges: { included: true, amount: 0 },
    driverCharges: { included: true, amount: 0 },
    nightCharges: { included: true, amount: 0 },
    features: [],
    active: true,
    originalImageUrl: ''
  });
  
  // State already declared at the top of the component

  const handleOpenModal = (cab = null) => {
    if (cab) {
      // Store the original cab object to preserve all data including the image URL
      setFormData({
        name: cab.name,
        description: cab.description,
        acType: cab.acType || 'AC',
        seatingCapacity: cab.seatingCapacity || 4,
        luggageCapacity: cab.luggageCapacity || 2,
        baseKmPrice: cab.baseKmPrice || '',
        extraFarePerKm: cab.extraFarePerKm || 0,
        includedKm: cab.includedKm || 0,
        vehicleLocation: cab.vehicleLocation || '',
        features: cab.features || [],
        fuelCharges: {
          included: cab.fuelCharges?.included !== undefined ? cab.fuelCharges.included : true,
          amount: cab.fuelCharges?.amount || 0
        },
        driverCharges: {
          included: cab.driverCharges?.included !== undefined ? cab.driverCharges.included : true,
          amount: cab.driverCharges?.amount || 0
        },
        nightCharges: {
          included: cab.nightCharges?.included !== undefined ? cab.nightCharges.included : true,
          amount: cab.nightCharges?.amount || 0
        },
        active: cab.active !== undefined ? cab.active : true,
        // Store the original image URL to preserve it if no new image is uploaded
        originalImageUrl: cab.imageUrl || ''
      });
      setCurrentCab(cab);
      setImagePreview(cab.imageUrl || '');
    } else {
      setFormData({
        name: '',
        description: '',
        acType: 'AC',
        seatingCapacity: 4,
        luggageCapacity: 2,
        baseKmPrice: '',
        extraFarePerKm: 0,
        includedKm: 0,
        vehicleLocation: '',
        features: [],
        fuelCharges: {
          included: true,
          amount: 0
        },
        driverCharges: {
          included: true,
          amount: 0
        },
        nightCharges: {
          included: true,
          amount: 0
        },
        active: true,
        originalImageUrl: ''
      });
      setCurrentCab(null);
      setImagePreview('');
      setImageFile(null);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentCab(null);
    setFormData({
      name: '',
      description: '',
      acType: 'AC',
      seatingCapacity: 4,
      luggageCapacity: 2,
      baseKmPrice: '',
      extraFarePerKm: 0,
      includedKm: 0,
      vehicleLocation: '',
      features: [],
      fuelCharges: {
        included: true,
        amount: 0
      },
      driverCharges: {
        included: true,
        amount: 0
      },
      nightCharges: {
        included: true,
        amount: 0
      },
      active: true,
      originalImageUrl: ''
    });
    setFeatureInput('');
    setSelectedImage(null);
  };

  // Handle feature input key down (add feature on Enter)
  const handleFeatureKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (featureInput.trim() !== '') {
        if (!formData.features.includes(featureInput.trim())) {
          setFormData({
            ...formData,
            features: [...formData.features, featureInput.trim()]
          });
          setFeatureInput('');
        }
      }
    }
  };

  // Handle adding a feature
  const handleAddFeature = () => {
    if (featureInput.trim() !== '') {
      if (!formData.features.includes(featureInput.trim())) {
        setFormData({
          ...formData,
          features: [...formData.features, featureInput.trim()]
        });
        setFeatureInput('');
      }
    }
  };

  // Handle removing a feature (supports both index and direct feature value)
  const handleRemoveFeature = (featureOrIndex) => {
    if (typeof featureOrIndex === 'number') {
      // If an index was passed
      setFormData(prev => ({
        ...prev,
        features: prev.features.filter((_, i) => i !== featureOrIndex)
      }));
    } else {
      // If a feature string was passed
      setFormData({
        ...formData,
        features: formData.features.filter(f => f !== featureOrIndex)
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle nested charge objects
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      // Handle regular fields
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? Number(value) : value)
      }));
    }
  };

  // Comprehensive handler for image uploads
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setSelectedImage(reader.result); // Also set the selectedImage for preview
      };
      reader.readAsDataURL(file);
    }
  };

  // For backward compatibility with existing code
  const handleFeatureChange = handleFeatureKeyDown;

  // For backward compatibility with existing code
  const removeFeature = handleRemoveFeature;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Cab name is required');
      return;
    }
    
    console.log('Submitting form data:', formData);
    // Show a single processing toast for the entire create/update flow
    toast.info('Processing cab... Please wait', { toastId: 'cab-process', autoClose: false });
    
    try {
      const token = localStorage.getItem('token') || 
        (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).token : null);
      
      if (!token) {
        toast.error('Authentication token not found', { toastId: 'auth-error' });
        return;
      }
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };
      
      // Step 1: Create a JSON object with the cab data
      // Check if the name already contains (AC) or (Non-AC)
      let cabName = formData.name;
      if (!cabName.includes('(AC)') && !cabName.includes('(Non-AC)')) {
        cabName = formData.acType === 'Non-AC' 
          ? `${cabName} (Non-AC)` 
          : `${cabName} (AC)`;
      }
      const nameWithAC = cabName;
      
      const cabData = {
        name: nameWithAC,
        description: formData.description || '',
        acType: formData.acType || 'AC',
        seatingCapacity: parseInt(formData.seatingCapacity) || 4,
        luggageCapacity: parseInt(formData.luggageCapacity) || 2,
        baseKmPrice: parseFloat(formData.baseKmPrice) || 0,
        extraFarePerKm: parseFloat(formData.extraFarePerKm) || 0,
        includedKm: parseInt(formData.includedKm) || 0,
        vehicleLocation: formData.vehicleLocation || '',
        fuelCharges: {
          included: formData.fuelCharges.included,
          amount: formData.fuelCharges.included ? 0 : parseFloat(formData.fuelCharges.amount) || 0
        },
        driverCharges: {
          included: formData.driverCharges.included,
          amount: formData.driverCharges.included ? 0 : parseFloat(formData.driverCharges.amount) || 0
        },
        nightCharges: {
          included: formData.nightCharges.included,
          amount: formData.nightCharges.included ? 0 : parseFloat(formData.nightCharges.amount) || 0
        },
        active: formData.active,
        features: formData.features || [],
        // Preserve existing image URL if updating and no new image is uploaded
        imageUrl: formData.directImageUrl || (currentCab && !imageFile ? formData.originalImageUrl : '')
      };
      
      console.log('Step 1: Creating cab with data:', cabData);
      
      let response;
      
      if (currentCab) {
        // Update existing cab
        response = await axios.put(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/cab/${currentCab._id}`,
          cabData,
          config
        );
        // Success will be shown after the entire flow completes (including optional image upload)
      } else {
        // Create new cab
        response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/cab/noimage`,
          cabData,
          config
        );
        // Success will be shown after the entire flow completes (including optional image upload)
      }
      
      console.log('Cab created/updated:', response.data);
      
      // Step 2: If there's an image file, upload it separately using the direct endpoint
      if (imageFile) {
        const cabId = response.data.data._id;
        console.log('Step 2: Uploading image for cab:', cabId);
        
        // Create a new FormData for the image only
        const imageFormData = new FormData();
        
        // Add the file with the name 'image' - this is critical
        imageFormData.append('image', imageFile);
        
        // Log FormData contents for debugging
        console.log('FormData created with file:', imageFile.name, 'size:', imageFile.size, 'type:', imageFile.type);
        
        // Update headers for multipart/form-data - don't set Content-Type manually
        // Let Axios set the correct multipart boundary
        const imageConfig = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        try {
          // Add loading indicator
          // Update processing toast to indicate image upload step
          toast.update('cab-process', { render: 'Uploading image...', type: 'info', isLoading: true, autoClose: false });
          
          // Upload the image using the direct endpoint
          const imageResponse = await axios.post(
            `${API_URL}/cab/${cabId}/image`,
            imageFormData,
            imageConfig
          );
          
          console.log('Image uploaded successfully:', imageResponse.data);
          // Update the single processing toast to indicate overall success
          toast.update('cab-process', { render: 'Cab saved and image uploaded successfully', type: 'success', isLoading: false, autoClose: 3000 });

          // Update the cab with the new image URL in the local state if needed
          if (imageResponse.data?.data?.imageUrl) {
            // This could update the UI to show the new image right away
            console.log('Updated image URL:', imageResponse.data.data.imageUrl);
          }
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          console.error('Error details:', imageError.response?.data || 'No response data');

          // More detailed error handling
          if (imageError.response?.status === 500) {
            toast.error(`Server error during image upload: ${imageError.response?.data?.message || 'Unknown error'}`, { toastId: 'upload-failed' });

            // Update the cab to let the user know it was created but image failed
            try {
              await axios.put(
                `${API_URL}/cab/${cabId}`,
                { ...cabData, note: 'Image upload failed - please try again' },
                config
              );
            } catch (e) {
              // Silently fail if this update fails
              console.error('Failed to update cab with note:', e);
            }
          } else if (imageError.response?.status === 400) {
            toast.update('cab-process', { render: `Cab saved but image upload failed: ${imageError.response?.data?.message || 'File validation failed'}`, type: 'warning', isLoading: false, autoClose: 5000 });
          } else {
            toast.update('cab-process', { render: `Cab saved but image upload failed (${imageError.response?.status || 'Unknown error'})`, type: 'warning', isLoading: false, autoClose: 5000 });
          }
        }
      } else {
        console.log('No image file provided');
        // Update the processing toast to success since no image upload is required
        toast.update('cab-process', { render: currentCab ? 'Cab updated successfully' : 'Cab created successfully', type: 'success', isLoading: false, autoClose: 3000 });
      }
      
      // Refresh the cab list
      dispatch(getAllCabs());
      
      // Close the modal
      handleCloseModal();
    } catch (error) {
      console.error('Error saving cab data:', error);
      const errMsg = error.response?.data?.message || error.message || 'Unknown error';
      // Update the processing toast to show the error
      toast.update('cab-process', { render: `Error saving cab: ${errMsg}`, type: 'error', isLoading: false, autoClose: 5000 });
    }
  };

  const toggleStatus = (cabId, currentActive) => {
    // Find the cab to update its status
    const cabToUpdate = cabs.find(cab => cab._id === cabId);
    if (!cabToUpdate) {
      toast.error('Cab not found', { toastId: 'cab-not-found' });
      return;
    }
    
    const newActive = !currentActive;
    
    // Create FormData for the update
    const formDataObj = new FormData();
    formDataObj.append('active', newActive.toString());
    
    // Update status using Redux action
    dispatch(updateCab({
      id: cabId,
      cabData: formDataObj
    }));
  };
  
  // Delete a cab
  const handleDelete = (cabId) => {
    if (!window.confirm('Are you sure you want to delete this cab?')) {
      return;
    }
    
    // Delete cab using Redux action
    dispatch(deleteCab(cabId));
  };

  const filteredCabs = cabs.filter(cab => 
    cab.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Cab Management</h1>
        <button 
          onClick={() => handleOpenModal()}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add New Cab
        </button>
      </div>
      
      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="w-full md:w-1/3">
            <label htmlFor="search" className="sr-only">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              <input
                id="search"
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search cabs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Cabs Grid */}
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCabs.length > 0 ? (
            filteredCabs.map((cab) => (
              <div key={cab._id} className={`bg-white rounded-lg shadow-md overflow-hidden ${cab.active ? '' : 'opacity-70'}`}>
                <div className="bg-gray-200 h-48 flex items-center justify-center">
                  {cab.imageUrl ? (
                    <img 
                      src={cab.imageUrl} 
                      alt={cab.name} 
                      className="h-full w-full object-cover" 
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x200?text=Cab+Image';
                      }}
                    />
                  ) : (
                    <svg className="h-24 w-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h2 className="text-xl font-semibold">{cab.name}</h2>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      cab.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {cab.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{cab.description}</p>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div>
                      <p className="text-gray-500 text-xs">Base Price</p>
                      <p className="font-medium">₹{cab.baseKmPrice}/km</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Extra KM Charge</p>
                      <p className="font-medium">₹{cab.extraFarePerKm || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Capacity</p>
                      <p className="font-medium">{cab.seatingCapacity} Persons</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Luggage</p>
                      <p className="font-medium">{cab.luggageCapacity} Bags</p>
                    </div>
                  </div>
                  
                  {/* Features */}
                  {cab.features && cab.features.length > 0 && (
                    <div className="mb-4">
                      <p className="text-gray-500 text-xs mb-1">Features</p>
                      <div className="flex flex-wrap gap-1">
                        {cab.features.map((feature, index) => (
                          <span key={index} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Charges information */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {cab.fuelCharges && cab.fuelCharges.included ? (
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                        Fuel Included
                      </span>
                    ) : (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                        Fuel: ₹{cab.fuelCharges?.amount || 0}
                      </span>
                    )}
                    {cab.driverCharges && cab.driverCharges.included ? (
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                        Driver Included
                      </span>
                    ) : (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                        Driver: ₹{cab.driverCharges?.amount || 0}
                      </span>
                    )}
                    {cab.nightCharges && cab.nightCharges.included ? (
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                        Night Charges Included
                      </span>
                    ) : (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                        Night: ₹{cab.nightCharges?.amount || 0}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={() => handleOpenModal(cab)}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm hover:bg-blue-200"
                    >
                      Edit
                    </button>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleDelete(cab._id)}
                        className="bg-red-100 text-red-800 px-3 py-1 rounded text-sm hover:bg-red-200"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => toggleStatus(cab._id, cab.active)}
                        className={`px-3 py-1 rounded text-sm ${
                          cab.active ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {cab.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              {searchTerm ? 'No cabs matching search criteria' : 'No cabs found'}
            </div>
          )}
        </div>
      )}
      
      {/* Add/Edit Cab Modal */}
      {modalOpen && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full">
              <div className="bg-gradient-to-b from-white to-gray-50 px-6 pt-6 pb-4 sm:p-6 sm:pb-4 border-b border-gray-100">
                <h3 className="text-xl leading-6 font-semibold text-blue-700 flex items-center">
                  {currentCab ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit Cab
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Add New Cab
                    </>
                  )}
                </h3>
                <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="md:col-span-2">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Cab Name*</label>
                      <div className="relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="block w-full pl-10 border border-gray-300 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 sm:text-sm"
                          placeholder="Enter cab model name"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="acType" className="block text-sm font-medium text-gray-700 mb-1">AC Type</label>
                      <div className="relative">
                        <select
                          name="acType"
                          id="acType"
                          value={formData.acType || 'AC'}
                          onChange={handleChange}
                          className="mt-1 block w-full appearance-none border border-gray-300 rounded-lg shadow-sm py-2.5 px-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 sm:text-sm"
                        >
                          <option value="AC">AC</option>
                          <option value="Non-AC">Non-AC</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  
                    <div className="md:col-span-2">
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <div className="relative rounded-md shadow-sm">
                        <textarea
                          name="description"
                          id="description"
                          rows="3"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder="Describe the cab and its key features"
                          className="block w-full border border-gray-300 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 sm:text-sm"
                        ></textarea>
                      </div>
                    </div>
                  </div>
                  
                  {/* Capacity and Luggage */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <h4 className="text-sm font-semibold text-blue-700 mb-3 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Cab Capacity Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="seatingCapacity" className="block text-sm font-medium text-gray-700 mb-1">
                          Seating Capacity
                        </label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <input
                            type="number"
                            name="seatingCapacity"
                            id="seatingCapacity"
                            value={formData.seatingCapacity}
                            onChange={handleChange}
                            min="1"
                            className="block w-full pl-10 pr-16 border border-gray-300 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 sm:text-sm"
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">persons</span>
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Maximum number of passengers</p>
                      </div>
                      <div>
                        <label htmlFor="luggageCapacity" className="block text-sm font-medium text-gray-700 mb-1">
                          Luggage Capacity
                        </label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <input
                            type="number"
                            name="luggageCapacity"
                            id="luggageCapacity"
                            value={formData.luggageCapacity}
                            onChange={handleChange}
                            min="0"
                            className="block w-full pl-10 pr-12 border border-gray-300 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 sm:text-sm"
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">bags</span>
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Maximum number of luggage items</p>
                      </div>
                    </div>
                  </div>

                  {/* Fares */}
                  <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                    <h4 className="text-sm font-semibold text-green-700 mb-3 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Pricing Information
                    </h4>
                    
                    <div className="bg-yellow-50 p-3 rounded-lg mb-4 border border-yellow-200">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3 text-sm text-yellow-700">
                          <p><strong>Pricing Update:</strong> The price calculation has been simplified to use a single rate per kilometer for the entire journey distance, making costs more predictable and transparent.</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label htmlFor="baseKmPrice" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                          Base Rate Per Kilometer
                          <span className="ml-1 text-gray-400 hover:text-gray-500 cursor-help" title="Standard price charged per kilometer for the entire journey">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </span>
                        </label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">₹</span>
                          </div>
                          <input
                            type="number"
                            name="baseKmPrice"
                            id="baseKmPrice"
                            value={formData.baseKmPrice}
                            onChange={handleChange}
                            placeholder="0.00"
                            className="block w-full pl-10 pr-16 border border-gray-300 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 sm:text-sm"
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">per km</span>
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">The price per kilometer applied to the entire journey distance</p>
                      </div>
                      
                      <div className="mt-4">
                        <label htmlFor="extraFarePerKm" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                          Extra KM Charge
                          <span className="ml-1 text-gray-400 hover:text-gray-500 cursor-help" title="Additional charge per kilometer after included kilometers (if any)">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </span>
                        </label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">₹</span>
                          </div>
                          <input
                            type="number"
                            name="extraFarePerKm"
                            id="extraFarePerKm"
                            value={formData.extraFarePerKm}
                            onChange={handleChange}
                            placeholder="0.00"
                            className="block w-full pl-10 pr-16 border border-gray-300 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 sm:text-sm"
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">per extra km</span>
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Additional charge per kilometer after included kilometers</p>
                      </div>
                      
                      <div className="mt-2 border-t border-gray-200 pt-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Example Calculation:</span>
                        </div>
                        <div className="mt-2 bg-white p-3 rounded-lg border border-gray-200">
                          <div className="flex justify-between text-sm">
                            <span>Base Rate:</span>
                            <span>₹{formData.baseKmPrice || 0}/km</span>
                          </div>
                          <div className="flex justify-between text-sm mt-1">
                            <span>Journey Distance:</span>
                            <span>1140 km</span>
                          </div>
                          <div className="flex justify-between font-medium text-sm mt-2 pt-2 border-t border-gray-100">
                            <span>Total Price:</span>
                            <span>₹{formData.baseKmPrice ? (formData.baseKmPrice * 1140).toLocaleString('en-IN') : 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Features */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <h4 className="text-sm font-semibold text-purple-700 mb-3 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      Cab Features
                    </h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.features.length > 0 ? (
                        formData.features.map((feature, index) => (
                          <div key={index} className="bg-white border border-purple-200 text-purple-800 px-3 py-1.5 rounded-full text-sm flex items-center shadow-sm transition-all duration-200 hover:shadow-md">
                            {feature}
                            <button
                              type="button"
                              onClick={() => handleRemoveFeature(index)}
                              className="ml-2 text-purple-400 hover:text-purple-700 focus:outline-none transition-colors duration-200"
                              aria-label={`Remove ${feature}`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 italic">No features added yet. Add features that make this cab special.</p>
                      )}
                    </div>
                    <div className="flex relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        value={featureInput}
                        onChange={(e) => setFeatureInput(e.target.value)}
                        onKeyDown={handleFeatureKeyDown}
                        placeholder="Type a feature and press Enter"
                        className="block w-full pl-10 border border-gray-300 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 sm:text-sm"
                      />
                      <button 
                        type="button"
                        onClick={() => {
                          if (featureInput.trim()) {
                            setFormData({
                              ...formData,
                              features: [...formData.features, featureInput.trim()]
                            });
                            setFeatureInput('');
                          }
                        }}
                        className="ml-2 bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition-colors duration-200 flex items-center">
                        <span>Add</span>
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">Common features: WiFi, Water Bottles, Music System, Charging Points</p>
                  </div>

                  
                  {/* Pickup Location */}
                  <div>
                    <label htmlFor="pickupLocation" className="block text-sm font-medium text-gray-700">
                    VehicleLocation
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="vehicleLocation"
                        id="vehicleLocation"
                        value={formData.vehicleLocation}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  {/* Fuel Charges Group */}
                  <div className="border rounded-md p-3 space-y-3">
                    <h4 className="font-medium text-gray-700 flex items-center">
                      Fuel Charges
                      <span className="ml-1 text-gray-400 hover:text-gray-500 cursor-help" title="Charges for fuel consumption during the trip">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                    </h4>
                    
                    <div className="flex items-center justify-between">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          name="fuelCharges.included"
                          checked={formData.fuelCharges.included}
                          onChange={handleChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Included in price</span>
                      </label>
                    
                      {!formData.fuelCharges.included && (
                        <div className="flex items-center">
                          <label htmlFor="fuelCharges.amount" className="mr-2 text-sm font-medium text-gray-700">
                            Charge:
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">₹</span>
                            </div>
                            <input
                              type="number"
                              name="fuelCharges.amount"
                              value={formData.fuelCharges.amount}
                              onChange={handleChange}
                              min="0"
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 pl-6 pr-10 w-36 sm:text-sm border-gray-300 rounded-md"
                            />
                            <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">per day</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Driver Charges Group */}
                  <div className="border rounded-md p-3 space-y-3">
                    <h4 className="font-medium text-gray-700 flex items-center">
                      Driver Charges
                      <span className="ml-1 text-gray-400 hover:text-gray-500 cursor-help" title="Charges for driver allowance and accommodation">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                    </h4>
                    
                    <div className="flex items-center justify-between">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          name="driverCharges.included"
                          checked={formData.driverCharges.included}
                          onChange={handleChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Included in price</span>
                      </label>
                    
                      {!formData.driverCharges.included && (
                        <div className="flex items-center">
                          <label htmlFor="driverCharges.amount" className="mr-2 text-sm font-medium text-gray-700">
                            Charge:
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">₹</span>
                            </div>
                            <input
                              type="number"
                              name="driverCharges.amount"
                              value={formData.driverCharges.amount}
                              onChange={handleChange}
                              min="0"
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 pl-6 pr-10 w-36 sm:text-sm border-gray-300 rounded-md"
                            />
                            <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">per day</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Night Charges Group */}
                  <div className="border rounded-md p-3 space-y-3">
                    <h4 className="font-medium text-gray-700 flex items-center">
                      Night Charges
                      <span className="ml-1 text-gray-400 hover:text-gray-500 cursor-help" title="Additional charges for night travel (typically between 10 PM - 6 AM)">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                    </h4>
                    
                    <div className="flex items-center justify-between">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          name="nightCharges.included"
                          checked={formData.nightCharges.included}
                          onChange={handleChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Included in price</span>
                      </label>
                    
                      {!formData.nightCharges.included && (
                        <div className="flex items-center">
                          <label htmlFor="nightCharges.amount" className="mr-2 text-sm font-medium text-gray-700">
                            Charge:
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">₹</span>
                            </div>
                            <input
                              type="number"
                              name="nightCharges.amount"
                              value={formData.nightCharges.amount}
                              onChange={handleChange}
                              min="0"
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 pl-6 pr-10 w-36 sm:text-sm border-gray-300 rounded-md"
                            />
                            <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">per night</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Active/Inactive */}
                  {/* Misc Options (Active Status) */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Cab Status
                    </h4>
                    <div className="flex items-center">
                      <div className="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
                        <input 
                          type="checkbox" 
                          name="active" 
                          id="active" 
                          checked={formData.active}
                          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                          className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer transition-transform duration-200"
                        />
                        <label 
                          htmlFor="active" 
                          className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${formData.active ? 'bg-green-500' : 'bg-gray-300'}`}
                        ></label>
                      </div>
                      <label htmlFor="active" className="text-sm font-medium text-gray-700 select-none">
                        {formData.active ? 'Active (Available for booking)' : 'Inactive (Hidden from users)'}
                      </label>
                    </div>
                  </div>

                  {/* Image Upload - Dual Approach */}
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
                    <h4 className="text-sm font-semibold text-yellow-700 mb-3 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Cab Image
                    </h4>
                    

                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-yellow-100 text-yellow-800 mr-2 text-xs">1</span>
                        Upload Image File
                      </h5>
                      <div className="mt-1 flex justify-center px-6 pt-4 pb-4 border-2 border-yellow-200 border-dashed rounded-lg">
                        <div className="space-y-1 text-center">
                          <svg
                            className="mx-auto h-10 w-10 text-yellow-400"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                            aria-hidden="true"
                          >
                            <path
                              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <div className="flex text-sm text-gray-600 justify-center">
                            <label
                              htmlFor="file-upload"
                              className="relative cursor-pointer bg-white rounded-md font-medium text-yellow-600 hover:text-yellow-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-yellow-500"
                            >
                              <span>Upload a file</span>
                              <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleImageChange} />
                            </label>
                            <p className="pl-1">or drag and drop</p>
                          </div>
                          <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                        </div>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 italic">Note: File upload may sometimes encounter server issues. Direct URL is more reliable.</p>
                    </div>
                    
                    {/* Preview */}
                    {(selectedImage || formData.imageUrl) && (
                      <div className="mt-3 p-2 border border-yellow-200 rounded-lg bg-white">
                        <h5 className="text-sm font-medium text-gray-700 mb-1">Image Preview:</h5>
                        <div className="flex justify-center">
                          <img 
                            src={selectedImage || formData.imageUrl} 
                            alt="Selected cab" 
                            className="h-36 object-cover rounded-md" 
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-8 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg shadow-md hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {currentCab ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        )}
                      </svg>
                      {currentCab ? 'Update Cab' : 'Add Cab'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cabs;
